import { Roles } from "@/enums";
import { Article } from "@/interfaces/Product";
import { Colis, IReturn } from "@/interfaces/Return";
import {
  PartialReceptionCounts,
  PartialReceptionPayload,
  RefusedArticle,
} from "@/interfaces/PartialReception";

export type ScanResult =
  | { status: "ok"; refusedArticle: RefusedArticle }
  | { status: "already_refused" }
  | { status: "not_found" };

export const canAccessPartialReception = (roles: string): boolean =>
  roles.includes(Roles.DELIVERY_ROLE) || roles.includes(Roles.HUB_ROLE);

// Le livreur reste prioritaire si un utilisateur cumule les deux rôles
export const usesHubPartialValidation = (roles: string): boolean =>
  roles.includes(Roles.HUB_ROLE) && !roles.includes(Roles.DELIVERY_ROLE);

export const isSpecialSellerReturn = (return_: IReturn): boolean =>
  return_.isSpecialSeller === true;

// Réception déjà enregistrée, il ne reste que le BL cacheté à joindre
export const isPartiallyReceived = (return_: IReturn): boolean =>
  return_.sellerReceptionPartial === true;

/** Un même produit peut exister dans plusieurs colis d'une tournée */
const buildArticleId = (colisId: number, productId: string): string =>
  `${colisId}-${productId}`;

const toRefusedArticle = (colis: Colis, article: Article): RefusedArticle => ({
  id: buildArticleId(colis.colisId, article.productId),
  colisId: colis.colisId,
  colisCode: colis.colisCode,
  article,
});

/** Tous les articles de la tournée, à plat, avec leur colis */
const listArticles = (
  colisList: Colis[]
): { colis: Colis; article: Article }[] =>
  (colisList ?? []).flatMap((colis) =>
    (colis.articles ?? []).map((article) => ({ colis, article }))
  );

/** Reprise: les articles refusés lors de la réception précédente */
export const listAlreadyRefusedArticles = (
  colisList: Colis[]
): RefusedArticle[] =>
  listArticles(colisList)
    .filter(({ article }) => article.isAccepted !== true)
    .map(({ colis, article }) => toRefusedArticle(colis, article));

export const findArticleToRefuse = (
  colisList: Colis[],
  barcode: string,
  refusedArticles: RefusedArticle[]
): ScanResult => {
  const matches = listArticles(colisList).filter(
    ({ article }) => article.genCode === barcode
  );

  if (matches.length === 0) {
    return { status: "not_found" };
  }

  const match = matches.find(
    ({ colis, article }) =>
      !refusedArticles.some(
        (refused) =>
          refused.id === buildArticleId(colis.colisId, article.productId)
      )
  );
  if (!match) {
    return { status: "already_refused" };
  }

  return {
    status: "ok",
    refusedArticle: toRefusedArticle(match.colis, match.article),
  };
};

export const countReception = (
  colisList: Colis[],
  refusedArticles: RefusedArticle[]
): PartialReceptionCounts => {
  const total = listArticles(colisList).length;

  return {
    total,
    refused: refusedArticles.length,
    accepted: total - refusedArticles.length,
  };
};

export const haveSameIds = (ids: string[], others: string[]): boolean =>
  ids.length === others.length && ids.every((id) => others.includes(id));

/** Tout ce qui n'a pas été scanné est reçu */
export const buildPartialReceptionPayload = (
  numTournee: number,
  colisList: Colis[],
  refusedIds: string[]
): PartialReceptionPayload => ({
  numTournee,
  detailRecepted: listArticles(colisList)
    .filter(
      ({ colis, article }) =>
        !refusedIds.includes(buildArticleId(colis.colisId, article.productId))
    )
    .map(({ colis, article }) => ({
      colisId: colis.colisId,
      productId: article.productId,
    })),
});
